<template>
  <div>
    <div class="row border-bottom white-bg dashboard-header">
      <span>Station Activity Dashboard</span>
    </div>
    <div class="wrapper wrapper-content animated fadeInRight">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group pull-left">
            <el-radio-group
              v-model="selectedRegion"
              @change.native="$emit('changeRegion', getRegion())"
            >
              <el-radio-button type="primary" label>All</el-radio-button>
              <el-radio-button
                v-for="region in regions"
                :key="region.code"
                type="primary"
                :label="region.code"
                >{{ region.name }}
              </el-radio-button>
            </el-radio-group>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group pull-right">
            <el-radio-group
              v-model="selectedRange"
              @change.native="$emit('changeDate', getDateRange())"
            >
              <el-radio-button label="30 Days" />
              <el-radio-button label="3 Months" />
              <el-radio-button label="6 Months" />
              <el-radio-button label="12 Months" />
            </el-radio-group>
          </div>
        </div>
      </div>
    </div>
    <div class="data-table-container">
      <Loader v-if="isLoading" msg="Loading Station Activity" />
      <el-table
        v-if="!isLoading"
        row-key="key"
        size="small"
        stripe
        :data="
          stationActivityData.results
            .filter(
              data =>
                !searchTerm ||
                data.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                data.organization.toLowerCase().includes(searchTerm.toLowerCase()),
            )
            .slice(0, 10)
        "
        :cell-style="cellStyle"
        :tree-props="{ children: 'users' }"
        class="data-table"
      >
        <el-table-column>
          <template slot="header">
            <input class="el-input__inner" v-model="searchTerm" placeholder="Station search" />
          </template>
          <el-table-column width="220" sortable prop="name" label="Station">
            <template slot-scope="scope">
              <span :class="{ 'bold-name': scope.row.is_admin }">{{ scope.row.name }}</span>
            </template>
          </el-table-column>
          <el-table-column width="220" sortable prop="name" label="Organization">
            <template slot-scope="scope">
              <span>{{ scope.row.organization }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column height="80" align="center" label="Users">
          <el-table-column
            width="120"
            sortable
            prop="last_activity"
            align="center"
            label="Last Activity"
          >
            <template slot-scope="scope">
              <span>{{ formatDate(scope.row.last_activity) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            width="120"
            sortable
            prop="no_users"
            align="center"
            label="No. Users"
          ></el-table-column>
          <el-table-column
            width="120"
            prop="perc_active"
            align="center"
            label="% Active"
          ></el-table-column>
        </el-table-column>
        <el-table-column align="center" label="Campaigns">
          <el-table-column
            width="120"
            prop="campaigns_last_created"
            align="center"
            label="Last Created"
          >
            <template slot-scope="scope">
              <span>{{ formatDate(scope.row.campaigns_last_created) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            width="120"
            prop="campaigns_no_created"
            align="center"
            label="No. Created"
          ></el-table-column>
          <el-table-column
            width="120"
            prop="campaigns_no_active"
            align="center"
            label="No. Active"
          ></el-table-column>
        </el-table-column>
        <el-table-column align="center" label="Surveys">
          <el-table-column
            width="120"
            prop="surveys_last_created"
            align="center"
            label="Last Created"
          >
            <template slot-scope="scope">
              <span>{{ formatDate(scope.row.surveys_last_created) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            width="120"
            prop="surveys_no_created"
            align="center"
            label="No. Created"
          ></el-table-column>
          <el-table-column
            width="120"
            prop="surveys_no_active"
            align="center"
            label="No. Active"
          ></el-table-column>
          <el-table-column
            width="120"
            prop="surveys_latest_n"
            align="center"
            label="Latest n"
          ></el-table-column>
          <el-table-column
            width="120"
            prop="surveys_avg_n"
            align="center"
            label="Avg n"
          ></el-table-column>
        </el-table-column>
        <el-table-column align="center" label="Messages">
          <el-table-column prop="messages" align="center" label="F" :render-header="renderIcon">
            <template slot-scope="scope" v-if="scope.row.messages && scope.row.messages.length > 0">
              <span
                v-if="scope.row.messages[0].facebook_post || scope.row.messages[0].facebook_comment"
                >{{
                  formatNumber(
                    scope.row.messages[0].facebook_post + scope.row.messages[0].facebook_comment,
                  )
                }}</span
              >
            </template>
          </el-table-column>
          <el-table-column
            prop="messages[0].tweet"
            align="center"
            label="T"
            :render-header="renderIcon"
          >
            <template slot-scope="scope" v-if="scope.row.messages && scope.row.messages.length > 0">
              <span>{{ formatNumber(scope.row.messages[0].tweet) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="messages[0].instagram"
            align="center"
            label="I"
            :render-header="renderIcon"
          >
            <template slot-scope="scope" v-if="scope.row.messages && scope.row.messages.length > 0">
              <span>{{ formatNumber(scope.row.messages[0].instagram) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="messages[0].whatsapp"
            align="center"
            label="W"
            :render-header="renderIcon"
          >
            <template slot-scope="scope" v-if="scope.row.messages && scope.row.messages.length > 0">
              <span>{{ formatNumber(scope.row.messages[0].whatsapp) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="messages[0].telegram"
            align="center"
            label="TE"
            :render-header="renderIcon"
          >
            <template slot-scope="scope" v-if="scope.row.messages && scope.row.messages.length > 0">
              <span>{{ formatNumber(scope.row.messages[0].telegram) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="messages[0].facebook_message"
            align="center"
            label="FM"
            :render-header="renderIcon"
          >
            <template slot-scope="scope" v-if="scope.row.messages && scope.row.messages.length > 0">
              <span>{{ formatNumber(scope.row.messages[0].facebook_message) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="messages[0].message"
            align="center"
            label="SMS"
            :render-header="renderIcon"
          >
            <template slot-scope="scope" v-if="scope.row.messages && scope.row.messages.length > 0">
              <span>{{ formatNumber(scope.row.messages[0].message) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="messages[0].email"
            align="center"
            label="E"
            :render-header="renderIcon"
          >
            <template slot-scope="scope" v-if="scope.row.messages && scope.row.messages.length > 0">
              <span>{{ formatNumber(scope.row.messages[0].email) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column align="center" label="Unique Users">
          <el-table-column prop="unique_users" align="center" label="F" :render-header="renderIcon">
            <template
              slot-scope="scope"
              v-if="scope.row.unique_users && scope.row.unique_users.length > 0"
            >
              <span
                v-if="
                  scope.row.unique_users[0].facebook_post ||
                    scope.row.unique_users[0].facebook_comment
                "
                >{{
                  formatNumber(
                    scope.row.unique_users[0].facebook_post +
                      scope.row.unique_users[0].facebook_comment,
                  )
                }}</span
              >
            </template>
          </el-table-column>
          <el-table-column
            prop="unique_users[0].tweet"
            align="center"
            label="T"
            :render-header="renderIcon"
          >
            <template
              slot-scope="scope"
              v-if="scope.row.unique_users && scope.row.unique_users.length > 0"
            >
              <span>{{ formatNumber(scope.row.unique_users[0].tweet) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="unique_users[0].instagram"
            align="center"
            label="I"
            :render-header="renderIcon"
          >
            <template
              slot-scope="scope"
              v-if="scope.row.unique_users && scope.row.unique_users.length > 0"
            >
              <span>{{ formatNumber(scope.row.unique_users[0].instagram) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="unique_users[0].whatsapp"
            align="center"
            label="W"
            :render-header="renderIcon"
          >
            <template
              slot-scope="scope"
              v-if="scope.row.unique_users && scope.row.unique_users.length > 0"
            >
              <span>{{ formatNumber(scope.row.unique_users[0].whatsapp) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="unique_users[0].telegram"
            align="center"
            label="TE"
            :render-header="renderIcon"
          >
            <template
              slot-scope="scope"
              v-if="scope.row.unique_users && scope.row.unique_users.length > 0"
            >
              <span>{{ formatNumber(scope.row.unique_users[0].telegram) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="unique_users[0].facebook_message"
            align="center"
            label="FM"
            :render-header="renderIcon"
          >
            <template
              slot-scope="scope"
              v-if="scope.row.unique_users && scope.row.unique_users.length > 0"
            >
              <span>{{ formatNumber(scope.row.unique_users[0].facebook_message) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="unique_users[0].message"
            align="center"
            label="SMS"
            :render-header="renderIcon"
          >
            <template
              slot-scope="scope"
              v-if="scope.row.unique_users && scope.row.unique_users.length > 0"
            >
              <span>{{ formatNumber(scope.row.unique_users[0].message) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="unique_users[0].email"
            align="center"
            label="E"
            :render-header="renderIcon"
          >
            <template
              slot-scope="scope"
              v-if="scope.row.unique_users && scope.row.unique_users.length > 0"
            >
              <span>{{ formatNumber(scope.row.unique_users[0].email) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column>
          <el-table-column
            width="100"
            prop="qualitative_surveys"
            align="center"
            label="Qualitative"
          >
            <template slot-scope="scope">
              <el-checkbox v-model="scope.row.qualitative_surveys" disabled></el-checkbox>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column>
          <el-table-column width="100" prop="is_streaming" align="center" label="Streaming">
            <template slot-scope="scope">
              <el-checkbox v-model="scope.row.is_streaming" disabled></el-checkbox>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column v-if="selectedRegion === 'ZA' || selectedRegion === ''">
          <el-table-column width="100" prop="afstereo" align="center" label="AFStereo">
            <template slot-scope="scope">
              <el-checkbox v-model="scope.row.afstereo" disabled></el-checkbox>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>
      <div class="pagination">
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="10"
          :total="stationActivityData.count"
          @current-change="$emit('changePage', getPageChange($event))"
        ></el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import countryCodesMixin from '@/components/common/countries/countryCodesMixin';
import Loader from '@/components/common/Loader';
import EmailIcon from '@/components/common/Icons/EmailIcon';
import FbIcon from '@/components/common/Icons/FacebookIcon';
import InstagramIcon from '@/components/common/Icons/InstagramIcon';
import SmsIcon from '@/components/common/Icons/SmsIcon';
import TwitterIcon from '@/components/common/Icons/TwitterIcon';
import WhatsappIcon from '@/components/common/Icons/WhatsappIcon';
import TelegramIcon from '@/components/common/Icons/TelegramIcon';

export default {
  name: 'app',
  mixins: [countryCodesMixin],
  components: {
    Loader,
    EmailIcon,
    FbIcon,
    InstagramIcon,
    SmsIcon,
    TwitterIcon,
    WhatsappIcon,
  },
  data() {
    return {
      searchTerm: '',
      filteredResults: [],
      selectedRange: '30 Days',
      selectedRegion: '',
      cellStyle: { background: 'none' },
    };
  },
  computed: {
    regions() {
      const regions = this.stationActivityData.results.map(station => station.region);
      return [...new Set(regions)]
        .sort()
        .map(region => this.getCountryByCode(region))
        .filter(region => region !== undefined && region !== null);
    },
  },
  props: {
    isLoading: {
      type: Boolean,
      default: () => false,
    },
    region: {
      type: String,
      default: () => '',
    },
    startDate: {
      type: Object,
      default: () => moment(),
    },
    endDate: {
      type: Object,
      default: () => moment(),
    },
    stationActivityData: {
      type: Object,
      default: () => ({
        results: [],
        limit: 10,
      }),
    },
    offset: {
      type: Object,
      default: () => ({
        results: [],
      }),
    },
  },
  methods: {
    getRegion() {
      return { region: this.selectedRegion };
    },
    getDateRange() {
      const range = this.selectedRange.split(' ');
      const count = range[0];
      const unit = range[1];
      return {
        startDate: moment().subtract(count, unit),
        endDate: moment(),
      };
    },
    getPageChange(page) {
      if (page > 1) {
        return page * 10 - 10;
      }
      return 0;
    },
    renderIcon(h, { column }) {
      const icon = this.getIcon(column.label);
      const tooltip = this.getIconTip(column.label);
      return h('el-tooltip', { attrs: { content: tooltip } }, [h(icon)]);
    },
    getIcon(name) {
      switch (name) {
        case 'F':
          return FbIcon;
        case 'T':
          return TwitterIcon;
        case 'I':
          return InstagramIcon;
        case 'W':
          return WhatsappIcon;
        case 'TE':
          return TelegramIcon;
        case 'FM':
          return FbIcon;
        case 'SMS':
          return SmsIcon;
        case 'E':
          return EmailIcon;
        default:
          return FbIcon;
      }
    },
    getIconTip(name) {
      switch (name) {
        case 'F':
          return 'Facebook Posts and Comments';
        case 'T':
          return 'Twitter';
        case 'I':
          return 'Instagram';
        case 'W':
          return 'WhatsApp';
        case 'TE':
          return 'Telegram';
        case 'FM':
          return 'Facebook Messages';
        case 'SMS':
          return 'SMS';
        case 'E':
          return 'Email';
        default:
          return '';
      }
    },
    formatDate(date) {
      return date ? moment(date).format('MM-DD-YY') : 'Date unavailable';
    },
    formatNumber(num) {
      return new Intl.NumberFormat('en-US', { style: 'decimal', useGrouping: true }).format(num);
    },
  },
};
</script>

<style lang="scss">
.bold-name {
  font-weight: bold;
}

.data-table-container {
  padding: 4px;

  .el-table .el-table__header-wrapper thead div {
    background: none;
  }

  .data-table {
    padding: 0px;
    background: none;

    th {
      border: 2px solid #ebeef5;
    }

    td {
      border: 2px solid #ebeef5;
    }
  }
}

.pagination {
  padding: 15px;
}
</style>



// WEBPACK FOOTER //
// StationActivityDashboard.vue?c21daa76